import React, { useEffect, useState } from "react";
import { Form } from "antd";
import Banner from "../../../views/components/sections/Banner";
import { usePageDetail, usePageSave } from "../../../hooks/api/page/hooks";
import { getSingleImageUrl } from "../../../lib/utils";
import { useMessageApi } from "../../../provider/AntdNotification";
import TabsContain from "../../../views/components/reuseComp/TabsContain";
import { useQueryState } from "nuqs";
import { IPageDetailPayload, TestimonialPage } from "../../../types/api/page";
import TestimonialListing from "./testimonialListing";

const TestimonialTabs: React.FC<{
  setBreadcrumb: React.Dispatch<React.SetStateAction<string>>;
}> = ({ setBreadcrumb }) => {
  const messageApi = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const activeTab = tab || "Banner";

  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  const [pageData, setPageData] = useState<TestimonialPage | null>(null);

  const { data } = usePageDetail("TESTIMONIAL");
  const { mutate: updatePage, isPending } = usePageSave({});

  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (data?.data?.pageType === "TESTIMONIAL") {
      setPageData(data.data as TestimonialPage);
    }
  }, [data]);

  const handleChangeTab = (section: string) => {
    setTab(section);
    setIsEditing(false);
    setBreadcrumb(section);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setReviewMode("list");
    setEditingReviewIndex(null);
    form.resetFields();
  };

  const handleSave = async () => {
    if (!pageData) {
      return;
    }

    if (!pageData) return;

    try {
      let values: any;

      if (reviewMode === "add" || reviewMode === "edit") {
        values = await form.validateFields([
          "name",
          "place",
          "image",
          "desc",
          "status",
        ]);
      } else {
        values = await form.validateFields(["banner"]);
      }

      const testimonialData = pageData.testimonial ?? {
        banner: { heading: "", image: "" },
        content: [],
      };

      const existingContent = testimonialData.content ?? [];

      let updatedContent = existingContent;

      if (reviewMode === "add") {
        updatedContent = [
          ...existingContent,
          {
            ...values,
            image: getSingleImageUrl(values.image),
            isDeleted: false,
          },
        ];
      }

      if (reviewMode === "edit" && editingReviewIndex !== null) {
        updatedContent = existingContent.map(
          (
            item: TestimonialPage["testimonial"]["content"][number],
            index: number,
          ) =>
            index === editingReviewIndex
              ? {
                  ...item,
                  ...values,
                  image: getSingleImageUrl(values.image),
                }
              : item,
        );
      }

      const payload: TestimonialPage = {
        _id: pageData._id,
        pageType: "TESTIMONIAL" as const,
        testimonial: {
          banner: {
            heading:
              values.banner?.heading ?? testimonialData.banner.heading ?? "",
            image: getSingleImageUrl(
              values.banner?.image ?? testimonialData.banner.image ?? "",
            ),
          },
          content: updatedContent,
        },
      };

      updatePage(payload, {
        onSuccess: (res) => {
          messageApi.success("Page updated successfully");

          if (res?.data?.pageType === "TESTIMONIAL") {
            setPageData(res.data);
          }

          handleCancel();
        },
        onError: (err) => {
          console.error("❌ API Error:", err);
          messageApi.error("Failed to update page");
        },
      });
    } catch (error) {
      console.error("❌ Validation failed:", error);
      messageApi.error("Please fill all required fields");
    }
  };

  const handleDeleteTestimonial = (id: string) => {
    if (!pageData) return;

    const updatedContent = pageData.testimonial.content.map(
      (item: TestimonialPage["testimonial"]["content"][number]) =>
        item._id === id
          ? {
              ...item,
              isDeleted: true,
              status: false,
            }
          : item,
    );

    updatePage(
      {
        _id: pageData._id,
        pageType: "TESTIMONIAL",
        testimonial: {
          ...pageData.testimonial,
          content: updatedContent,
        },
      },
      {
        onSuccess: (res) => {
          messageApi.success("Testimonial deleted successfully");

          if (res?.data?.pageType === "TESTIMONIAL") {
            setPageData(res.data);
          }
        },
        onError: (err) => {
          messageApi.error("Failed to delete testimonial");
          console.error(err);
        },
      },
    );
  };

  const sections = ["Banner", "Testimonials"];

  return (
    <div>
      <TabsContain
        sections={sections}
        handleChangeTab={handleChangeTab}
        activeTab={activeTab}
      />

      {activeTab === "Banner" && (
        <Banner
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          bannerData={pageData?.testimonial?.banner}
          loading={isPending}
        />
      )}

      {activeTab === "Testimonials" && (
        <TestimonialListing
          testimonialList={pageData?.testimonial?.content}
          form={form}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
          onDelete={handleDeleteTestimonial}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}
    </div>
  );
};

export default TestimonialTabs;
